import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Avatar,
  AvatarBadge,
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  Input,
  InputGroup,
  InputLeftAddon,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
  Wrap
} from "@chakra-ui/react";
import { getUserByIdAsAdvisor, sendProspectToCRM, transferAccess, updateClient } from "../../../api/user-service";
import { ClientType, IHouseholdMember, IMemberType } from "../../../types/user";
import moment from "moment/moment";
import UpdateClientModal, { Gender, MaritalStatus } from "../../admin/modals/components/UpdateClientModal";
import TransferOwnerShipModal from "../../admin/modals/components/TransferOwnershipModal";
import { getBadgeColor } from "../../../libraries/utils";
import { MdSend } from "react-icons/md";
import { useAuth } from "../../../auth-context/auth.context";

const ClientDetail = () => {
  const { id }: { id: string } = useParams();
  const history = useHistory();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState<ClientType | undefined>(undefined);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isTransferOpen, onOpen: onTransferOpen, onClose: onTransferClose } = useDisclosure();
  const { isOpen: isCRMOpen, onOpen: onCRMOpen, onClose: onCRMClose } = useDisclosure();
  const [selectedCRM, setSelectedCRM] = useState<any>(null);
  const [isSending, setIsSending] = useState(false);

  const { user: globalUser } = useAuth();

  useEffect(() => {
    fetchClient(id);
  }, [id]);

  const fetchClient = (id: string) => {
    setIsLoading(true);
    getUserByIdAsAdvisor(id)
      .then(({ user }) => {
        setUser(user);
      })
      .catch(err => {
        console.error(err);
      })
      .finally(() => {
        setIsLoading(false);
      })
  }
  const handleSendCRM = async () => {
    if (!selectedCRM) {
      toast({
        title: "Select CRM",
        description: "Please select a CRM first.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSending(true);

    try {
      await sendProspectToCRM(id, selectedCRM);

      toast({
        title: "CRM Sync",
        description: `Prospect Information sent to ${selectedCRM} successfully!`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      onCRMClose();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || error?.message || "Something went wrong while syncing to CRM.";

      toast({
        title: "CRM Sync Failed",
        description: errorMessage,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSending(false);
    }
  };

  const canSendToCRM = globalUser?.accessTokens?.length > 0;

  const updateClientInfo = async ({
                                    addressLine1,
                                    dateOfBirth,
                                    city,
                                    companyName,
                                    firstName,
                                    lastName,
                                    management,
                                    middleName,
                                    nickName,
                                    gender,
                                    maritalStatus,
                                    phoneNumber,
                                    postalCode,
                                    state,
                                    userRole,
                                    ownBusiness,
                                    hasBusinessLiabilities,
                                    estimatedBusinessValue,
                                    estimatedDebt,
                                    spouseName,
                                    spouseDob,
                                    note,
                                    householdMembers,
                                  }: {
    firstName: string,
    middleName: string,
    lastName: string,
    nickName: string,
    dateOfBirth: string,
    gender: Gender,
    maritalStatus: MaritalStatus,
    phoneNumber: string,
    userRole: string,
    management: string,
    addressLine1: string,
    city: string,
    state: string,
    postalCode: string,
    companyName: string,
    ownBusiness: boolean,
    hasBusinessLiabilities?: boolean,
    estimatedBusinessValue?: string | null,
    estimatedDebt?: string | null,
    spouseName?: string,
    spouseDob?: string | null,
    note?: string,
    householdMembers?: IHouseholdMember[] | [],
  }) => {
    try {
      await updateClient(
        id, 
        householdMembers || [], {
        addressLine1,
        dateOfBirth,
        city,
        fullName: companyName,
        firstName,
        lastName,
        gender,
        maritalStatus,
        management,
        middleName,
        nickName,
        phoneNumber,
        postalCode,
        state,
        accountType: userRole,
        ownBusiness,
        hasBusinessLiabilities,
        estimatedBusinessValue,
        estimatedDebt,
        spouseName,
        spouseDob,
        note,
      })
      toast({
        title: 'Update Prospect',
        description: `${firstName}'s information has been updated.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      fetchClient(id);
    } catch (error: any) {
      toast({
        title: 'Update Prospect',
        description: error.response?.data.message ?? error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  };


  const transfer = async ({
                            fromUserId,
                            toUserId,
                            toUserName,
                          }: {
    fromUserId: number,
    toUserId: number,
    toUserName: string,
  }) => {
    try {
      await transferAccess(user?.userId, fromUserId, toUserId);
      toast({
        title: 'Access Transfer',
        description: `${user?.firstName}'s access transferred to ${toUserName}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

    } catch (error: any) {
      toast({
        title: 'Access Transfer',
        description: error.response?.data.message ?? error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      {
        isLoading ? <Flex justify="center" align="center" height="100vh">
            <CircularProgress size={12} isIndeterminate color="green.300"/>
          </Flex> :
          <Box>
            <Grid
              gap={4}
              templateRows={{
                base: 'none',
                lg: '1fr',
              }}
              templateColumns={{
                base: 'none',
                lg: 'repeat(4, 1fr)',
              }}>
              <GridItem colSpan={1}>
                <Flex align="center" direction='column' mt="20px">
                  <Tooltip label={user?.receivedInvitations?.[0].accepted ? 'Joined' : 'Not Joined'}
                           placement='right'>
                    <Avatar
                      src={user?.avatarUrl}
                      name={`${user?.firstName} ${user?.lastName}`}
                      size="xl"
                    >
                      <AvatarBadge boxSize='1em'
                                   bg={user?.receivedInvitations?.[0].accepted ? 'green.500' : 'red.500'}/>
                    </Avatar>
                  </Tooltip>
                  <Text mt={4} fontWeight="500">Client
                    from: {moment(user?.receivedInvitations?.[0].createdAt).format('MMMM DD, YYYY')}</Text>
                </Flex>
              </GridItem>
              <GridItem colSpan={{ base: 1, lg: 3 }}>
                <Flex justifyContent="right" alignItems="center" mb={4}>
                  {canSendToCRM && (
                    <Button
                      colorScheme="blue"
                      variant="solid"
                      rightIcon={<MdSend/>}
                      onClick={onCRMOpen}
                    >
                      Send to CRM
                    </Button>
                  )}
                </Flex>
                <Text fontSize="2xl" fontWeight="bold">Prospect Details</Text>

                <Grid
                  templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
                  gap={{ base: 4, md: 8 }}
                >
                  <FormControl>
                    <FormLabel>First Name</FormLabel>
                    <Input readOnly defaultValue={user?.firstName}/>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Middle Name</FormLabel>
                    <Input readOnly defaultValue={user?.middleName}/>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Last Name</FormLabel>
                    <Input readOnly defaultValue={user?.lastName}/>
                  </FormControl>
                </Grid>
                <Grid mt="16px"
                      templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
                      gap={{ base: 4, md: 8 }}
                >
                  <FormControl>
                    <FormLabel>Company Name</FormLabel>
                    <Input readOnly defaultValue={user?.fullName}/>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Gender</FormLabel>
                    <Select isReadOnly isDisabled defaultValue={user?.gender || 'Unknown'}>
                      <option key={0} value="">
                        Select a gender...
                      </option>
                      <option value="Female">Female</option>
                      <option value="Male">Male</option>
                      <option value="Non-binary">Non-binary</option>
                      <option value="Unknown">Prefer not to say</option>
                    </Select>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Marital Status</FormLabel>
                    <Select isReadOnly isDisabled defaultValue={user?.maritalStatus || 'Unknown'}>
                      <option key={0} value="">
                        Select a status...
                      </option>
                      <option value="Married">Married</option>
                      <option value="Single">Single</option>
                      <option value="Divorced">Divorced</option>
                      <option value="Widowed">Widowed</option>
                      <option value="Life Partner">Life Partner</option>
                      <option value="Separated">Separated</option>
                      <option value="Unknown">Prefer not to say</option>
                    </Select>
                  </FormControl>
                </Grid>

                {user?.maritalStatus === 'Married' && (
                  <Grid
                    templateColumns="repeat(2, 1fr)"
                    w="full"
                    gap={4}
                    mt="16px"
                  >
                    <GridItem colSpan={{ base: 2, md: 1 }}>
                      <FormLabel fontWeight="500" color="gray.700">
                        Spouse Name
                      </FormLabel>
                      <Input readOnly defaultValue={user?.spouseName}/>
                    </GridItem>
                    <GridItem colSpan={{ base: 2, md: 1 }}>
                      <FormLabel fontWeight="500" color="gray.700">
                        Spouse Dob
                      </FormLabel>
                      <Input readOnly
                             defaultValue={user?.spouseDob ? moment(user?.spouseDob, 'YYYY-MM-DD').format('MM/DD/YYYY') : ''}/>
                    </GridItem>
                  </Grid>
                )}
                <Grid mt="16px"
                      templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
                      gap={{ base: 4, md: 8 }}
                >
                  <FormControl>
                    <FormLabel>Date of Birth</FormLabel>
                    <Input readOnly
                           defaultValue={user?.dateOfBirth ? moment(user?.dateOfBirth, 'YYYY-MM-DD').format('MM/DD/YYYY') : ''}/>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Email</FormLabel>
                    <Input readOnly defaultValue={user?.email}/>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Phone Number</FormLabel>
                    <Input readOnly defaultValue={user?.phoneNumber}/>
                  </FormControl>
                </Grid>
                <Grid mt="16px"
                      templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
                      gap={{ base: 4, md: 8 }}
                >
                  <GridItem colSpan={{ base: 1, md: 2 }}>
                    <FormControl>
                      <FormLabel>Address</FormLabel>
                      <Input readOnly defaultValue={user?.addressLine1}/>
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>City</FormLabel>
                      <Input readOnly defaultValue={user?.city}/>
                    </FormControl>
                  </GridItem>
                </Grid>
                <Grid mt="16px"
                      templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
                      gap={{ base: 4, md: 8 }}
                >
                  <FormControl>
                    <FormLabel>State</FormLabel>
                    <Input readOnly defaultValue={user?.state}/>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Zip Code</FormLabel>
                    <Input readOnly defaultValue={user?.postalCode}/>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Country</FormLabel>
                    <Input readOnly defaultValue={user?.country}/>
                  </FormControl>
                </Grid>

                <Grid mt="16px"
                      templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
                      gap={{ base: 4, md: 8 }}
                >
                  <Box mt='16px'>
                    <Text fontWeight="600" fontSize="md">
                      Connected CRM
                    </Text>
                    <HStack mt={2}>
                      {user?.wbContactId && (
                        <Badge colorScheme={getSourceColor('Wealthbox')} variant="outline">
                          Wealthbox
                        </Badge>
                      )}
                      {user?.rtContactId && (
                        <Badge colorScheme={getSourceColor('Redtail')} variant="outline">
                          Redtail
                        </Badge>
                      )}
                      {user?.sfContactId && (
                        <Badge colorScheme={getSourceColor('Salesforce')} variant="outline">
                          Salesforce
                        </Badge>
                      )}
                    </HStack>
                  </Box>

                  <Box mt='16px'>
                    <Text fontWeight="600" fontSize="md">
                      Created by
                    </Text>
                    <Badge colorScheme={getSourceColor(user?.source)}
                           variant='outline'>
                      {user?.source}
                    </Badge>
                  </Box>
                </Grid>

                <HStack mt={4} justify="start" w="100%">
                  <Text fontWeight="600" fontSize="lg">
                    Do you own a business?
                  </Text>
                  <Badge colorScheme={user?.ownBusiness ? 'green' : 'red'}>{user?.ownBusiness ? 'Yes' : 'No'}</Badge>
                </HStack>

                {user?.ownBusiness && (
                  <Box w="full">
                    <HStack mt={2} justify="start" w="full">
                      <Text fontWeight="500" fontSize="md">
                        Do You Have Any Liabilities / Debt?
                      </Text>
                      <Badge
                        colorScheme={user?.hasBusinessLiabilities ? 'green' : 'red'}>{user?.hasBusinessLiabilities ? 'Yes' : 'No'}</Badge>
                    </HStack>
                    <Grid
                      templateColumns="repeat(2, 1fr)"
                      w="full"
                      gap={4}
                      mt="4px"
                    >
                      {user?.hasBusinessLiabilities && (
                        <GridItem colSpan={{ base: 2, md: 1 }}>
                          <FormLabel
                            fontSize="md"
                            fontWeight="500"
                            color="gray.700"
                          >
                            Estimated total debt/liabilities (optional)
                          </FormLabel>

                          <InputGroup>
                            <InputLeftAddon>$</InputLeftAddon>
                            <Input readOnly defaultValue={user?.estimatedDebt}/>
                          </InputGroup>
                        </GridItem>
                      )}

                      <GridItem colSpan={{ base: 2, md: 1 }}>
                        <FormLabel
                          fontSize="md"
                          fontWeight="500"
                          color="gray.700"
                        >
                          Estimated Business Value (optional)
                        </FormLabel>

                        <InputGroup>
                          <InputLeftAddon>$</InputLeftAddon>
                          <Input readOnly defaultValue={user?.estimatedBusinessValue}/>
                        </InputGroup>
                      </GridItem>
                    </Grid>
                  </Box>
                )}

                <FormControl mt='16px'>
                  <FormLabel>Note</FormLabel>
                  <Text color='gray.600'>{user?.note}</Text>
                </FormControl>
                <Button mt='16px' colorScheme="brand" variant="solid" maxW="120px" onClick={onOpen}>
                  Update
                </Button>
                <Box mt="32px" w="full">
                  <Text fontSize='xl' fontWeight='500'>Advisor Information</Text>
                  <Divider/>
                </Box>
                <Flex direction={{ base: 'column', md: 'row' }} py='16px' gap={2} align='center'
                      justify={{ base: 'center', md: 'space-between' }}>
                  <Wrap>
                    <FormLabel>Name: </FormLabel>
                    <Text>{user?.receivedInvitations?.[0].sender?.firstName} {user?.receivedInvitations?.[0].sender?.lastName}</Text>
                  </Wrap>
                  <Wrap>
                    <FormLabel>Email: </FormLabel>
                    <Text>{user?.receivedInvitations?.[0].sender?.email}</Text>
                  </Wrap>
                  <Button variant="solid" size='sm' colorScheme="brand" onClick={onTransferOpen}>
                    Access Transfer
                  </Button>
                </Flex>
                {user && user.members.length > 0 && (
                  <Box mt="32px" w="full">
                    <Text fontSize='xl' fontWeight='500'>Members Information</Text>
                  <Divider/>
                </Box>
                )}
                <Flex direction='column'>
                  {user && user.members.map((m: IMemberType) => (
                  <Flex direction={{ base: 'column', md: 'row' }} py='16px' gap={2} align='center'
                        justify={{ base: 'center', md: 'space-between' }}>
                    <Wrap>
                      <FormLabel>Name: </FormLabel>
                      <Text>{m.firstName} {m.lastName}</Text>
                    </Wrap>
                    <Wrap>
                      <FormLabel>Email: </FormLabel>
                      <Text>{m.email}</Text>
                    </Wrap>
                    <Wrap>
                      <FormLabel>Relation: </FormLabel>
                      <Text>{m.relationType}</Text>
                    </Wrap>
                  </Flex>
                  ))}
                </Flex>
                {
                  user?.smarttaxClients?.length > 0 &&

                  <Box mt="32px" w="full">
                    <Text fontSize='xl' fontWeight='500'>Smarttax Client Information</Text>
                    <Divider/>
                    {
                      user?.smarttaxClients.map((client: any) => (
                        <Grid mt="16px"
                              templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
                              gap={{ base: 2, md: 4 }}
                              borderWidth='1px'
                              p={2}
                              borderRadius={4}
                        >
                          <Wrap>
                            <FormLabel>Org. Type: </FormLabel>
                            <Text>{client?.organizationType}</Text>
                          </Wrap>
                          <Wrap>
                            <FormLabel>Title: </FormLabel>
                            <Text>{client?.title}</Text>
                          </Wrap>
                          <Wrap>
                            <FormLabel>Status: </FormLabel>
                            <Badge colorScheme={getBadgeColor(client?.status)}>{client?.status}</Badge>
                          </Wrap>
                          <Wrap>
                            <FormLabel>EIN: </FormLabel>
                            <Text>{client?.lastFourEIN ? `** ***${client?.lastFourEIN}` : '--'}</Text>
                          </Wrap>
                          <Wrap>
                            <FormLabel>SSN: </FormLabel>
                            <Text>{client?.lastFourSSN ? `** ***${client?.lastFourSSN}` : '--'}</Text>
                          </Wrap>
                          {
                            client?.isCompany &&
                            <Wrap>
                              <FormLabel>Business Name: </FormLabel>
                              <Text>{client?.businessName}</Text>
                            </Wrap>
                          }
                        </Grid>
                      ))
                    }
                  </Box>
                }
                <Flex justify="start" w='full' mt={4}>
                  <Button colorScheme="brand" variant="outline" minW="120px" onClick={() => history.goBack()}>
                    Back
                  </Button>
                </Flex>
              </GridItem>
            </Grid>
          </Box>
      }
      {
        user && <UpdateClientModal isOpen={isOpen} onClose={onClose} onSave={updateClientInfo} user={user}/>
      }
      {
        user &&
        <TransferOwnerShipModal isOpen={isTransferOpen} onClose={onTransferClose} onSave={transfer} client={user}/>
      }

      <Modal isOpen={isCRMOpen} onClose={onCRMClose} isCentered>
        <ModalOverlay/>
        <ModalContent>
          <ModalHeader>Select CRM</ModalHeader>
          <ModalCloseButton/>
          <ModalBody>
            <Text mb={4}>Choose a CRM to send this prospect:</Text>
            {/* Display radio buttons dynamically, based on access tokens */}
            <RadioGroup colorScheme="brand" onChange={setSelectedCRM} value={selectedCRM}>
              <Stack direction="column">
                {canSendToCRM ? (
                  globalUser.accessTokens.map((token: any) => (
                    <Radio key={token.provider} value={token.provider}>
                      {token.provider}
                    </Radio>
                  ))
                ) : (
                  <Text color="gray.500">No connected CRMs found. Please <Link href='/admin/settings?tab=Integration'
                                                                               color='blue.500'>connect</Link> a CRM
                    first.</Text>
                )}
              </Stack>
            </RadioGroup>
            <Text mt={4} hidden={!canSendToCRM}>Do you confirm sending prospect information to {selectedCRM}?</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" colorScheme='brand' onClick={onCRMClose}>
              Cancel
            </Button>
            <Button colorScheme="brand" ml={3} onClick={handleSendCRM} isLoading={isSending}>
              Send
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

const getSourceColor = (source?: 'internal' | 'smartleads' | 'Wealthbox' | 'Redtail' | 'API' | 'Salesforce'): string => {
  switch (source) {
    case 'internal':
      return 'green';
    case 'smartleads':
      return 'orange';
    case 'Wealthbox':
      return 'navy';
    case 'Redtail':
      return 'red';
    case 'API':
      return 'purple';
    case 'Salesforce':
      return 'blue';
    default:
      return 'red';
  }
};

export default ClientDetail;
